import React, { useContext, useEffect, useState } from 'react';

import MetricsComponent from './Metrics';
import BoardContext from '../../../contexts/BoardContext';
import Loading from '../../Loading/Loading';
import dateRangeInputToAmericanLabel from '../../../dateRangeInputToAmericanLabel';
import isDefined from '../../../isDefined';
import MetricOptionsContext from '../../../contexts/MetricOptionsContext';
import Typography from 'kingpin/atoms/Typography';
import GqlClientContext from '../../../contexts/GqlClientContext';
import useNetworkingEffect from '../../../hooks/useNetworkingEffect';
import isPerformanceBoard from '../../../isPerformanceBoard';
import runAggregation from './runAggregation';
import useMetricAggregationRequest from 'hooks/useMetricAggregationRequest';

const useMetricList = (
  metricList: Metrics.Metric[],
  filterInput: FilterInput,
  dateScope: DateRangeInput,
) => {
  const { client } = useContext(GqlClientContext);
  const { buildMetricsAndExpressions } = useMetricAggregationRequest();
  const [data, setData] = useState<MetricsResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useNetworkingEffect(() => {
    let isActive = true;
    setIsLoading(true);
    const {
      metrics: queryMetrics,
      expressions: queryExpressions,
      ready,
    } = buildMetricsAndExpressions(metricList);

    // Hold (stay loading) until every compound input has resolved — otherwise
    // the request carries the expression with a partial `metrics` list, which
    // the backend rejects ("parse error: Expected EOF").
    if (!ready || queryMetrics.length === 0) {
      return;
    }
    runAggregation({
      client,
      metrics: queryMetrics,
      expressions: queryExpressions,
      filterInput: filterInput ? [filterInput] : [],
      dateScope,
    }).then((response) => {
      if (isActive) {
        setData(response);
        setIsLoading(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [buildMetricsAndExpressions, filterInput, metricList, dateScope, client]);

  return {
    data,
    isLoading,
  };
};

export const MetricsContainer = ({
  filterInput,
  dateScope,
  metricIds,
  title,
  homeViz,
}: {
  filterInput: FilterInput;
  dateScope: DateRangeInput;
  metricIds: string[];
  title: string;
  homeViz?: boolean;
}) => {
  const { metricOptions } = useContext(MetricOptionsContext);
  const [metrics, setMetrics] = useState<Metrics.Metric[]>([]);
  const { data, isLoading: isLoadingData } = useMetricList(
    metrics,
    filterInput,
    dateScope,
  );

  useEffect(() => {
    const ms = metricIds
      .map((mId) => metricOptions.find((m) => m.id === mId))
      .filter(isDefined);
    setMetrics(ms);
  }, [metricOptions, metricIds]);

  if (isLoadingData || !data) {
    return (
      <>
        <Typography.Header type="H5">{title}</Typography.Header>
        <Loading isSmall />
      </>
    );
  }

  const dateRange = dateRangeInputToAmericanLabel(dateScope);

  return (
    <MetricsComponent
      metrics={metrics}
      data={data}
      dateRange={dateRange}
      title={title}
      homeViz={homeViz}
    />
  );
};

const Gate = ({
  filterInput,
  dateScope,
  title,
}: {
  filterInput?: FilterInput;
  dateScope?: DateRangeInput;
  title: string;
}) => {
  const { board } = useContext(BoardContext);
  if (isPerformanceBoard(board)) {
    return null;
  }

  if (board.metricIds && !!filterInput && !!dateScope) {
    return (
      <MetricsContainer
        filterInput={filterInput}
        dateScope={dateScope}
        metricIds={board.metricIds}
        title={title}
      />
    );
  } else {
    return null;
  }
};

export default Gate;
